import { useState } from "react";
import { ScrollView, View, Text, Pressable, ActivityIndicator } from "react-native";
import Unicon from "@/components/General/Unicon";
import EmptyListPlaceholder from "./EmptyListPlaceholder";
import { useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import PageIndicator from "./PageIndicator";
import usePlatform from "@/utils/hooks/usePlatform";
import FeatureIcon from "./FeatureIcon";
import CutCard from "./CutCard";
import dashboardStyles from "@/components/Dashboard/dashboardStyles";

const PagerCard = ({ title, icon, feature, testID, pages, isLoading = false, emptyText, linkRoute = null, linkRouteParams = {}, linkText = "", filterOptions = null, activeFilter = null, onFilterChange = null }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [contentWidth, setContentWidth] = useState(0);

    const router = useRouter();
    const { isNative } = usePlatform();

    const hasPages = pages && pages.length > 0;
    const hasFilters = filterOptions && filterOptions.length > 1;

    return (
        <CutCard testID={testID} style={[styles.container(!!feature), feature && dashboardStyles.card]} contentStyle={feature ? dashboardStyles.content : styles.content}>
            {/* Header */}
            <View style={[styles.header, feature && dashboardStyles.header]}>
                <View style={[styles.titleRow, feature && dashboardStyles.titleRow]}>
                    {feature ? <FeatureIcon feature={feature} /> : icon && (
                        <View style={styles.iconWrap}>
                            <Unicon name={icon} size={14} colorVariant="primary" />
                        </View>
                    )}
                    <Text style={[styles.title, feature && dashboardStyles.title]}>{title}</Text>
                </View>
                {linkRoute && (
                    <Pressable hitSlop={20} onPress={() => router.push({ pathname: linkRoute, params: linkRouteParams })} style={styles.linkBtn}>
                        <Text style={styles.linkText}>{linkText}</Text>
                        <Unicon name="chevron-forward-outline" size={12} colorVariant="primary" />
                    </Pressable>
                )}
            </View>

            {/* Filter Pills */}
            {hasFilters && (
                <View style={feature && dashboardStyles.filterStrip}>
                    <ScrollView
                        horizontal
                        style={feature && dashboardStyles.filterStrip}
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.filterScroll}
                        keyboardShouldPersistTaps="handled"
                    >
                        {filterOptions.map((filter) => (
                            <Pressable
                                key={filter}
                                testID={`PagerCard-filter-${filter}`}
                                style={[
                                    styles.filterPill,
                                    activeFilter === filter && styles.filterPillActive,
                                ]}
                                onPress={() => onFilterChange?.(filter)}
                            >
                                <Text
                                    style={[
                                        styles.filterPillText,
                                        activeFilter === filter && styles.filterPillTextActive,
                                    ]}
                                >
                                    {filter}
                                </Text>
                            </Pressable>
                        ))}
                    </ScrollView>
                </View>
            )}

            {/* Content */}
            {isLoading ? (
                <View style={[styles.loadingWrap, !feature && styles.content]}>
                    <ActivityIndicator size="small" />
                </View>
            ) : hasPages ? (
                <ScrollView
                    testID={testID && `${testID}-scroll`}
                    onLayout={(e) => setContentWidth(e.nativeEvent?.layout?.width)}
                    horizontal={isNative}
                    key={pages.length}
                    style={feature && dashboardStyles.scroll}
                    pagingEnabled={isNative}
                    onMomentumScrollEnd={(e) => setCurrentPage(Math.round((e.nativeEvent?.contentOffset?.x) / contentWidth))}
                    showsHorizontalScrollIndicator={false}
                    decelerationRate="fast"
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled
                >
                    {pages.map((page, index) => (
                        isNative && feature ? (
                            <ScrollView
                                key={index}
                                testID={testID && `${testID}-page-${index}`}
                                style={[styles.pageWrap(contentWidth), dashboardStyles.scroll]}
                                showsVerticalScrollIndicator={false}
                                keyboardShouldPersistTaps="handled"
                                nestedScrollEnabled
                            >
                                {page}
                            </ScrollView>
                        ) : (
                            <View key={index} style={styles.pageWrap(contentWidth)}>
                                {page}
                            </View>
                        )
                    ))}
                </ScrollView>
            ) : (
                <EmptyListPlaceholder size={isNative ? 140 : 90} type="complete" message={emptyText} spacing={8} />
            )}

            {/* Page dots */}
            {hasPages && pages.length > 1 && isNative && (
                <View style={feature && dashboardStyles.filterStrip}>
                    <PageIndicator pageCount={pages.length} currentPage={currentPage} />
                </View>
            )}
        </CutCard>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    container: (isDashboard) => ({
        maxHeight: isDashboard ? undefined : 500,
        minHeight: isDashboard ? undefined : 290,
        width: {
            xs: "100%",
            lg: "48.5%",
        },
        borderRadius: roundness.md,
        padding: spacing.md,
        paddingBottom: spacing.xs,
        backgroundColor: colors.surface,
        boxShadow: isDashboard ? shadows.none : shadows.card,
        overflow: "visible",
        _web: {
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            _hover: { boxShadow: isDashboard ? shadows.none : shadows.cardHover },
        },
    }),
    content: {
        flex: 1,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: spacing.sm,
    },
    titleRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs + 2,
    },
    iconWrap: {
        width: 24,
        height: 24,
        borderRadius: 6,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    title: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    linkBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.xs + 2,
        borderRadius: 6,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    linkText: {
        color: colors.primary,
        fontSize: typography.caption,
        fontFamily: fonts.heading,
    },
    loadingWrap: {
        alignItems: "center",
        justifyContent: "center",
    },
    filterScroll: {
        flexDirection: "row",
        gap: spacing.xs,
        marginBottom: spacing.sm,
    },
    filterPill: {
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        borderRadius: 20,
        backgroundColor: colors.surfaceVariant,
        borderWidth: 1,
        borderColor: colors.border,
        alignSelf: "flex-start",
    },
    filterPillActive: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
    },
    filterPillText: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    filterPillTextActive: {
        color: colors.onPrimary,
        fontFamily: fonts.bold,
    },
    pageWrap: (contentWidth) => ({
        width: contentWidth,
    }),
}));

export default PagerCard;